/**
 * Offline Sync Service
 * Caches data for offline access and manages sync state
 */

import { put, putMany, getAll, getByIndex, get, clear, remove, STORES } from './offlineDB';
import { apiHelpers } from './api';

// How far back to cache data (30 days)
const CACHE_DAYS = 30;

/**
 * Calculate the cutoff date for caching
 */
function getCacheStartDate() {
  const date = new Date();
  date.setDate(date.getDate() - CACHE_DAYS);
  return date.toISOString();
}

/**
 * Cache all data for an organization
 */
export async function cacheOrgData(orgId, options = {}) {
  const { onProgress } = options;
  const startDate = getCacheStartDate();
  
  console.log('[OfflineSync] Caching data for org:', orgId, 'from:', startDate);

  const steps = [
    { name: 'clients', weight: 1 },
    { name: 'estimates', weight: 2 },
    { name: 'jobs', weight: 1 },
    { name: 'invoices', weight: 1 },
  ];
  
  const totalWeight = steps.reduce((sum, s) => sum + s.weight, 0);
  let completedWeight = 0;

  try {
    // Cache clients
    const clientsRes = await apiHelpers.getClients(orgId);
    await putMany(STORES.CLIENTS, clientsRes.data.map(c => ({ ...c, _cached_at: new Date().toISOString() })));
    completedWeight += 1;
    onProgress?.({ step: 'clients', progress: completedWeight / totalWeight });

    // Cache estimates (filter by date on client side for now)
    const estimatesRes = await apiHelpers.getEstimates(orgId);
    const recentEstimates = estimatesRes.data.filter(e => 
      !e.created_at || e.created_at >= startDate
    );
    await putMany(STORES.ESTIMATES, recentEstimates.map(e => ({ ...e, _cached_at: new Date().toISOString() })));
    completedWeight += 2;
    onProgress?.({ step: 'estimates', progress: completedWeight / totalWeight });

    // Cache active jobs
    const jobsRes = await apiHelpers.getJobs(orgId);
    const activeJobs = jobsRes.data.filter(j => 
      ['scheduled', 'in_progress'].includes(j.status) || 
      (j.start_date && j.start_date >= startDate.split('T')[0])
    );
    await putMany(STORES.JOBS, activeJobs.map(j => ({ ...j, _cached_at: new Date().toISOString() })));
    completedWeight += 1;
    onProgress?.({ step: 'jobs', progress: completedWeight / totalWeight });

    // Cache invoices
    const invoicesRes = await apiHelpers.getInvoices(orgId);
    const recentInvoices = invoicesRes.data.filter(i => 
      !i.created_at || i.created_at >= startDate
    );
    await putMany(STORES.INVOICES, recentInvoices.map(i => ({ ...i, _cached_at: new Date().toISOString() })));
    completedWeight += 1;
    onProgress?.({ step: 'invoices', progress: completedWeight / totalWeight });

    // Update sync metadata
    await put(STORES.SYNC_META, {
      key: `last_sync_${orgId}`,
      timestamp: new Date().toISOString(),
      org_id: orgId,
    });

    console.log('[OfflineSync] Cache complete');
    return true;
  } catch (error) {
    console.error('[OfflineSync] Cache failed:', error);
    throw error;
  }
}

/**
 * Cache organization settings
 */
export async function cacheOrgSettings(org) {
  await put(STORES.ORG_SETTINGS, { ...org, _cached_at: new Date().toISOString() });
}

/**
 * Cache user settings
 */
export async function cacheUserSettings(user) {
  await put(STORES.USER_SETTINGS, { ...user, _cached_at: new Date().toISOString() });
}

/**
 * Get cached clients for an org
 */
export async function getCachedClients(orgId) {
  return getByIndex(STORES.CLIENTS, 'org_id', orgId);
}

/**
 * Get cached estimates for an org
 */
export async function getCachedEstimates(orgId) {
  return getByIndex(STORES.ESTIMATES, 'org_id', orgId);
}

/**
 * Get cached jobs for an org
 */
export async function getCachedJobs(orgId) {
  return getByIndex(STORES.JOBS, 'org_id', orgId);
}

/**
 * Get cached invoices for an org
 */
export async function getCachedInvoices(orgId) {
  return getByIndex(STORES.INVOICES, 'org_id', orgId);
}

/**
 * Get cached site visits for an org
 */
export async function getCachedSiteVisits(orgId) {
  return getByIndex(STORES.SITE_VISITS, 'org_id', orgId);
}

/**
 * Cache a single site visit
 */
export async function cacheSiteVisit(siteVisit) {
  await put(STORES.SITE_VISITS, { ...siteVisit, _cached_at: new Date().toISOString() });
}

/**
 * Save field notes draft locally (for offline persistence)
 */
export async function saveFieldNotesDraft(siteVisitId, notes, orgId) {
  const draft = {
    id: `draft_${siteVisitId}`,
    site_visit_id: siteVisitId,
    org_id: orgId,
    notes,
    updated_at: new Date().toISOString(),
    synced: false,
  };
  await put(STORES.FIELD_NOTES_DRAFTS, draft);
  console.log('[OfflineSync] Field notes draft saved:', siteVisitId);
  return draft;
}

/**
 * Get field notes draft for a site visit
 */
export async function getFieldNotesDraft(siteVisitId) {
  return get(STORES.FIELD_NOTES_DRAFTS, `draft_${siteVisitId}`);
}

/**
 * Mark field notes draft as synced (or remove it)
 */
export async function clearFieldNotesDraft(siteVisitId) {
  await remove(STORES.FIELD_NOTES_DRAFTS, `draft_${siteVisitId}`);
  console.log('[OfflineSync] Field notes draft cleared:', siteVisitId);
}

/**
 * Get all unsynced field notes drafts
 */
export async function getUnsyncedFieldNotesDrafts() {
  const allDrafts = await getAll(STORES.FIELD_NOTES_DRAFTS);
  return allDrafts.filter(d => !d.synced);
}

/**
 * Get cached org settings
 */
export async function getCachedOrgSettings(orgId) {
  return get(STORES.ORG_SETTINGS, orgId);
}

/**
 * Get cached user settings
 */
export async function getCachedUserSettings(userId) {
  return get(STORES.USER_SETTINGS, userId);
}

/**
 * Get last sync time for an org
 */
export async function getLastSyncTime(orgId) {
  const meta = await get(STORES.SYNC_META, `last_sync_${orgId}`);
  return meta?.timestamp || null;
}

/**
 * Clear all cached data
 */
export async function clearAllCachedData() {
  await clear(STORES.CLIENTS);
  await clear(STORES.ESTIMATES);
  await clear(STORES.JOBS);
  await clear(STORES.INVOICES);
  await clear(STORES.ORG_SETTINGS);
  await clear(STORES.USER_SETTINGS);
  await clear(STORES.SYNC_META);
  console.log('[OfflineSync] All cached data cleared');
}

/**
 * Get cache statistics
 */
export async function getCacheStats(orgId) {
  const clients = await getCachedClients(orgId);
  const estimates = await getCachedEstimates(orgId);
  const jobs = await getCachedJobs(orgId);
  const invoices = await getCachedInvoices(orgId);
  const lastSync = await getLastSyncTime(orgId);

  return {
    clients: clients.length,
    estimates: estimates.length,
    jobs: jobs.length,
    invoices: invoices.length,
    lastSync,
  };
}
